import { isMinigameType } from '@/components/challenges/challengeKinds';
import { ChallengeDemo } from '@/components/challenges/demos/ChallengeDemo';
import type { DemoChallenge } from '@/components/challenges/demos/types';
import { minigameAuthoringRegistry } from '@/components/challenges/minigames';
import {
  FormFieldGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@startupquickstart/frontend';
import { useMemo } from 'react';
import type { ChallengeEditorSectionProps } from '../types';
import { SectionCard } from './SectionCard';

/** Preview id — the demo never talks to the server. */
const PREVIEW_CHALLENGE_ID = 'editor-preview';

const PUZZLE_GRID_OPTIONS = [
  { value: 3, label: '3×3 (Easy)' },
  { value: 4, label: '4×4 (Hard)' },
] as const;

/**
 * Minigame/puzzle authoring: the per-type config panel on the left with a
 * live playable `<ChallengeDemo>` preview on the right, driven by the same
 * unsaved config the admin is editing.
 */
export function MinigameSection({ values, errors, updateField }: ChallengeEditorSectionProps) {
  const isPuzzle = values.type === 'puzzle';
  const Panel = isMinigameType(values.type)
    ? minigameAuthoringRegistry[values.type]
    : undefined;

  const demoChallenge: DemoChallenge = useMemo(
    () => ({
      id: PREVIEW_CHALLENGE_ID,
      type: values.type,
      answerType: values.answerType,
      isTimed: values.isTimed,
      imageUrl: values.imageId || null,
      puzzleGridSize: values.puzzleGridSize,
      minigameConfig: values.minigameConfig,
    }),
    [
      values.type,
      values.answerType,
      values.isTimed,
      values.imageId,
      values.puzzleGridSize,
      values.minigameConfig,
    ],
  );

  return (
    <SectionCard
      id="minigame"
      title={isPuzzle ? 'Puzzle Setup' : 'Minigame Setup'}
      description={
        isPuzzle
          ? 'Grid size and timing for the sliding puzzle (built from the challenge image)'
          : 'Configure the minigame and try it exactly as participants will play it'
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          {isPuzzle ? (
            <>
              <FormFieldGroup
                name="puzzleGridSize"
                label="Puzzle Grid Size"
                description="Grid the challenge image is sliced into"
              >
                <Select
                  value={String(values.puzzleGridSize)}
                  onValueChange={(v) => updateField('puzzleGridSize', parseInt(v, 10))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select grid size" />
                  </SelectTrigger>
                  <SelectContent>
                    {PUZZLE_GRID_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormFieldGroup>

              <div className="flex items-start justify-between gap-6 rounded-lg border bg-muted/20 p-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Timed Challenge</p>
                  <p className="text-sm text-muted-foreground">
                    Track how long teams take to complete this challenge.
                  </p>
                </div>
                <Switch
                  checked={values.isTimed}
                  onCheckedChange={(checked) => updateField('isTimed', checked)}
                />
              </div>

              {errors.imageId && (
                <p className="text-sm text-destructive">{errors.imageId}</p>
              )}
            </>
          ) : Panel ? (
            <>
              <Panel
                value={values.minigameConfig}
                onChange={(next) => updateField('minigameConfig', next)}
              />
              {errors.minigameConfig && (
                <p id="field-minigameConfig" className="text-sm text-destructive">
                  {errors.minigameConfig}
                </p>
              )}
            </>
          ) : null}
        </div>

        <div className="min-w-0">
          <p className="mb-2 font-mono text-xs uppercase tracking-wider text-muted-foreground">
            Live preview
          </p>
          <ChallengeDemo challenge={demoChallenge} />
        </div>
      </div>
    </SectionCard>
  );
}
